import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCompanyNotificationsViewModel } from "../viewModel/notification.viewmodel";
import Pagination from "../../JobManagement/Components/Pagination";

export const CompanyNotification = () => {
  const {
    state,
    dispatch,
    fetchNotifications,
    handleMarkAsRead,
    handleMarkAllRead,
  } = useCompanyNotificationsViewModel();
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const navigate = useNavigate();
  const handleActionClick = (url: string) => {
    if (url === "/company/flowsetting") {
      navigate(url, { state: { activeTab: "Subscription" } });
    } else {
      navigate(url);
    }
  };

  const buildParams = (page = 1, currentFilter = filter) => {
    const params: any = {
      limit: state.limit,
      offset: (page - 1) * state.limit,
    };
    if (state.priority) params.priority = state.priority;
    if (state.notification_type)
      params.notification_type = state.notification_type;
    if (currentFilter === "unread") params.is_read = false;
    return params;
  };

  const fetchNotificationsWithParams = (page = 1, currentFilter = filter) => {
    fetchNotifications(buildParams(page, currentFilter));
  };

  const handleFilterChange = (value: "all" | "unread") => {
    setFilter(value);
    dispatch({ type: "SET_CURRENT_PAGE", payload: 1 });
    fetchNotificationsWithParams(1, value);
  };

  useEffect(() => {
    fetchNotificationsWithParams(state.currentPage);
  }, []);
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "text-red-600 bg-red-50 border-red-200";
      case "high":
        return "text-orange-600 bg-orange-50 border-orange-200";
      case "medium":
        return "text-blue-600 bg-blue-50 border-blue-200";
      case "low":
        return "text-gray-600 bg-gray-50 border-gray-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "recruiter_message":
        return "💬";
      case "interview_invitation":
        return "📅";
      case "profile_view":
        return "👁️";
      case "resume_download":
        return "📄";
      case "job_match":
        return "🎯";
      case "application_update":
        return "📋";
      case "skill_verification":
        return "🏆";
      case "background_check":
        return "✅";
      default:
        return "📢";
    }
  };
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Notifications
        </h1>
        <p className="text-gray-600 mt-2 text-sm sm:text-base">
          Stay updated with your job search activity
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 ">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">Filter:</label>
            <select
              value={filter}
              onChange={(e) =>
                handleFilterChange(e.target.value as "all" | "unread")
              }
              className="border border-gray-300 rounded-md  py-1 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">All notifications</option>
              <option value="unread">Unread only</option>
            </select>
            <span className="text-sm text-gray-600">
              {state.total} total • {state.unread_count} unread
            </span>
          </div>
          {state.unread_count > 0 && (
            <button
              onClick={() => handleMarkAllRead()}
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            >
              Mark all as read
            </button>
          )}
        </div>
      </div>

      {/* Notification List */}
      <div className="space-y-3">
        {state.loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : state.notifications.length === 0 ? (
          <p className="text-center text-gray-500 py-10">
            No notifications found.
          </p>
        ) : (
          <div className="space-y-3 mb-8">
            {state.notifications.map((notification) => (
              <div
                key={notification.id}
                //onClick={() => handleOpenNotification(notification)}
                className={`cursor-pointer bg-white border rounded-lg p-4 transition-all hover:shadow-sm ${
                  notification.is_read
                    ? "border-gray-200"
                    : "border-emerald-200 bg-emerald-50/30"
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3
                          className={`font-semibold text-sm truncate ${
                            notification.is_read
                              ? "text-gray-700"
                              : "text-gray-900"
                          }`}
                        >
                          {notification.title}
                        </h3>

                        <span
                          className={`text-xs px-2 py-1 rounded-full border flex-shrink-0 ${getPriorityColor(
                            notification.priority,
                          )}`}
                        >
                          {notification.priority}
                        </span>
                        {!notification.is_read && (
                          <span className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0"></span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {notification.message}
                      </span>
                      {/* Brief message */}
                      <p
                        className={`text-xs ${
                          notification.is_read
                            ? "text-gray-500"
                            : "text-gray-600"
                        } mb-1`}
                      >
                        {/* {truncateMessage(notification.message)} */}
                      </p>

                      <div className="flex items-center space-x-3 text-xs text-gray-400">
                        <span>
                          {new Date(notification.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 sm:ml-4">
                    {notification.action_text && notification.action_url && (
                      <button
                        onClick={() =>
                          handleActionClick(notification.action_url!)
                        }
                        className="text-xs text-emerald-600 hover:text-emerald-700 font-medium px-2 py-1 rounded border border-emerald-200 hover:bg-emerald-50 transition-colors"
                      >
                        {notification.action_text}
                      </button>
                    )}
                    {!notification.is_read && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification.id);
                        }}
                        className="text-xs px-2 py-1 rounded border text-emerald-600 hover:text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      >
                        Mark as read
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          // <div
          //   key={n.id}
          //   className={`bg-white rounded-lg border p-4 shadow-sm ${
          //     !n.is_read ? "border-l-4 border-l-indigo-500" : ""
          //   }`}
          // >
          //   <div className="flex items-start justify-between gap-2">
          //     <div>
          //       <p className="text-sm font-medium text-gray-900">{n.title}</p>
          //       <p className="text-xs text-gray-600 mt-1">{n.message}</p>
          //     </div>
          //     <span
          //       className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          //         n.priority === "high"
          //           ? "bg-red-100 text-red-700"
          //           : n.priority === "medium"
          //           ? "bg-yellow-100 text-yellow-700"
          //           : "bg-gray-100 text-gray-600"
          //       }`}
          //     >
          //       {n.priority}
          //     </span>
          //   </div>
          //   <p className="text-xs text-gray-400 mt-2">
          //     {new Date(n.created_at).toLocaleString()}
          //   </p>
          // </div>
        )}
        {state.total > state.limit && (
          <div className="mt-6 flex justify-center items-center px-4 overflow-x-auto">
            <Pagination
              postsPerPage={state.limit}
              totalPosts={state.total}
              currentPage={state.currentPage}
              setCurrentPage={(page) => {
                const pageNumber =
                  typeof page === "function" ? page(state.currentPage) : page;
                dispatch({ type: "SET_CURRENT_PAGE", payload: pageNumber });
                fetchNotificationsWithParams(pageNumber, filter);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
